import React, { useMemo } from 'react';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import {
  MatterServiceGetDocOrFormDocument,
  MatterServiceMatterDocsAndFormsDocument,
  MatterServiceOrder_By,
  MatterServiceMatterDocsAndFormsFolders_Order_By,
  TrueDocsAndFormsSearchByIdDocument,
  MatterServiceMatterDocsAndForms_Order_By,
  MatterServiceMatterDocsAndFormsFolderFragment,
  MatterServiceMatterDocsAndFormFragment,
  TrueDocsAndFormsFragment,
  MatterServiceMatterDocsAndFormsStatuses_Enum,
  UpdateMatterServiceFormDataDocument,
  UpdateMatterServiceFormStatusDocument,
  UpdateMatterServiceKeepUpdatedWithMatterDocument,
} from '__generated__/graphql';
import { useSortedMatterDocuments } from 'components/matters/matter/documents_and_forms/sortedMatterDocumentsAtom';
import { sortBy } from 'lodash/fp';
import dayjs from 'dayjs';

export function useKeepUpdatedWithMatter(id: string) {
  const [mutation, mutationResult] = useMutation(
    UpdateMatterServiceKeepUpdatedWithMatterDocument,
  );
  const mutate = React.useCallback(
    async (keepUpdatedWithMatter: boolean) => {
      return mutation({
        variables: { id, keepUpdatedWithMatter },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: MatterServiceGetDocOrFormDocument, variables: { id } },
        ],
      });
    },
    [id, mutation],
  );
  return [mutate, mutationResult];
}

export function useUpdateFormData(id: string) {
  const [mutation, mutationResult] = useMutation(
    UpdateMatterServiceFormDataDocument,
  );
  const { status: currentStatus, matterId } = useForm(id)!;

  const matterDocsAndFormsFoldersRefetch =
    useMatterDocsAndFormsFoldersRefetch(matterId);

  const mutate = React.useCallback(
    async (
      data: any,
      status = currentStatus === 'NEW' ? 'DRAFT' : currentStatus,
    ) => {
      return mutation({
        variables: {
          id,
          data,
          status,
          isDuplicate: false,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: MatterServiceGetDocOrFormDocument, variables: { id } },
          matterDocsAndFormsFoldersRefetch,
        ],
      });
    },
    [id, currentStatus, mutation, matterDocsAndFormsFoldersRefetch],
  );

  return [mutate, mutationResult];
}

export function useUpdateStatusForForm(
  id: string,
  currentStatus: MatterServiceMatterDocsAndFormsStatuses_Enum,
  matterId: string,
) {
  const [mutation, result] = useMutation(UpdateMatterServiceFormStatusDocument);

  const matterDocsAndFormsFoldersRefetch =
    useMatterDocsAndFormsFoldersRefetch(matterId);

  const mutate = React.useCallback(
    async (status = currentStatus === 'NEW' ? 'DRAFT' : currentStatus) => {
      return mutation({
        variables: { id, status },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: MatterServiceGetDocOrFormDocument, variables: { id } },
          matterDocsAndFormsFoldersRefetch,
        ],
      });
    },
    [id, currentStatus, mutation, matterDocsAndFormsFoldersRefetch],
  );
  return [mutate, result];
}

export function useForm(id: string) {
  const { data } = useSuspenseQuery(MatterServiceGetDocOrFormDocument, {
    variables: { id },
  });

  return React.useMemo(() => {
    return data?.matterServiceMatterDocsAndForms_by_pk;
  }, [data]);
}

export function useFormData(id: string) {
  const form = useForm(id);
  const [updateFormData, loading] = useUpdateFormData(id);
  return [form?.data, updateFormData, loading];
}

export function useDocumentsFromContentfulIds(contentfulIds: string[] = []) {
  const { data } = useSuspenseQuery(TrueDocsAndFormsSearchByIdDocument, {
    variables: {
      contentfulIds,
    },
  });

  return data?.trueDocsAndFormsSearch?.edges || [];
}

interface DocsAndFormsFragmentWithDocument
  extends MatterServiceMatterDocsAndFormFragment {
  document: TrueDocsAndFormsFragment;
}

export interface DocsAndFormsFolderFragmentWithItems
  extends MatterServiceMatterDocsAndFormsFolderFragment {
  items: DocsAndFormsFragmentWithDocument[];
  updatedAt?: string;
}

const docsByName = sortBy((item: DocsAndFormsFragmentWithDocument) => {
  return `${item.document.formNumber} ${item.document.title}`;
});

const foldersByName = sortBy((item: DocsAndFormsFolderFragmentWithItems) => {
  return item.name;
});

function useMatterDocsandFormsFolderVariables(matterId?: string) {
  const sort = useSortedMatterDocuments(matterId || '');

  const orderBy = useMemo<
    | MatterServiceMatterDocsAndForms_Order_By
    | { name: MatterServiceOrder_By }
    | undefined
  >(() => {
    switch (sort) {
      case 'date-added':
        return {
          createdAt: MatterServiceOrder_By.Desc,
        } as MatterServiceMatterDocsAndForms_Order_By;
      case 'date-modified':
        return {
          updatedAt: MatterServiceOrder_By.Desc,
        } as MatterServiceMatterDocsAndForms_Order_By;
      case 'name':
        return {
          name: MatterServiceOrder_By.Asc,
        };
      default:
        return undefined;
    }
  }, [sort]);

  const sortKey = useMemo(() => {
    return Object.keys(orderBy as object)[0];
  }, [orderBy]);

  const defaultOrder = useMemo(
    () =>
      orderBy && sortKey !== 'name'
        ? orderBy
        : {
            createdAt: MatterServiceOrder_By.Asc,
          },
    [orderBy, sortKey],
  );

  return {
    sortKey,
    variables: {
      matterId,
      folderOrderBy: [
        defaultOrder as MatterServiceMatterDocsAndFormsFolders_Order_By,
      ],
      itemOrderBy: [defaultOrder as MatterServiceMatterDocsAndForms_Order_By],
    },
  };
}

function filterForDocuments(form: any) {
  if (!form.document) {
    console.info('No Contentful Document found', { form });
  }
  return !!form.document;
}

export function useMatterDocsAndFormsFolders(matterId: string) {
  const { sortKey, variables } = useMatterDocsandFormsFolderVariables(matterId);

  const {
    data: {
      matterServiceMatterDocsAndForms,
      matterServiceMatterDocsAndFormsFolders,
    },
  } = useSuspenseQuery(MatterServiceMatterDocsAndFormsDocument, {
    variables,
  });

  const allContentfulIds = React.useMemo(
    () => [
      ...matterServiceMatterDocsAndForms.map(d => d.contentfulId),
      ...matterServiceMatterDocsAndFormsFolders
        .map(folder => folder.items.map(d => d.contentfulId))
        .flat(),
    ],
    [matterServiceMatterDocsAndForms, matterServiceMatterDocsAndFormsFolders],
  );

  const allIds = React.useMemo(
    () => [
      ...matterServiceMatterDocsAndForms.map(d => d.id),
      ...matterServiceMatterDocsAndFormsFolders
        .map(folder => folder.items.map(d => d.id))
        .flat(),
    ],
    [matterServiceMatterDocsAndForms, matterServiceMatterDocsAndFormsFolders],
  );

  const documents = useDocumentsFromContentfulIds(allContentfulIds);

  const { noFolders, folders } = React.useMemo(() => {
    const noFolders = matterServiceMatterDocsAndForms
      .map(item => {
        return {
          ...item,
          matterId,
          document: documents.find(
            result => result!.contentfulId === item.contentfulId,
          ),
        };
      })
      .filter(filterForDocuments);

    const folders = matterServiceMatterDocsAndFormsFolders.map(folder => {
      const mostRecentItemInFolder = folder.items.reduce((current, item) => {
        return dayjs(item?.updatedAt).valueOf() >
          dayjs(current?.updatedAt).valueOf()
          ? item
          : current;
      }, folder.items[0]);
      return {
        ...folder,
        updatedAt: mostRecentItemInFolder?.updatedAt,
        accountLastEditorId: mostRecentItemInFolder?.accountLastEditorId,
        items: folder.items
          .map(item => ({
            ...item,
            document: documents.find(
              result => result!.contentfulId === item.contentfulId,
            ),
          }))
          .filter(filterForDocuments),
      };
    });

    return {
      noFolders: sortKey === 'name' ? docsByName(noFolders) : noFolders,
      folders: sortKey === 'name' ? foldersByName(folders) : folders,
    };
  }, [
    matterId,
    matterServiceMatterDocsAndForms,
    matterServiceMatterDocsAndFormsFolders,
    documents,
    sortKey,
  ]);

  const all = React.useMemo(
    () => [...noFolders, ...folders.map(folder => folder.items).flat()],
    [noFolders, folders],
  );

  return {
    noFolders,
    folders,
    allIds,
    allContentfulIds,
    all,
  };
}

export function useMatterDocsAndFormsFoldersRefetch(matterId?: string) {
  const { variables } = useMatterDocsandFormsFolderVariables(matterId);

  return {
    query: MatterServiceMatterDocsAndFormsDocument,
    variables,
  };
}

export function useLazyMatterDocsAndFormsFoldersRefetch() {
  const matterDocsAndFormsFoldersRefetch =
    useMatterDocsAndFormsFoldersRefetch();

  return (matterId: string) => ({
    ...matterDocsAndFormsFoldersRefetch,
    variables: {
      ...matterDocsAndFormsFoldersRefetch.variables,
      matterId,
    },
  });
}
